(function () {
  if (customElements.get('homepage-intro')) return;

  const WHEEL_OPTS = { passive: true, capture: true };

  class HomepageIntro extends HTMLElement {
    /**
     * @returns {boolean}
     */
    static #isEmailSignupHash() {
      const hash = window.location.hash;
      return Boolean(hash && hash.toLowerCase() === '#email-signup');
    }

    /** @type {ReturnType<typeof setTimeout> | null} */
    #timeoutId = null;
    /** @type {ReturnType<typeof setTimeout> | null} */
    #fallbackDismissId = null;
    #dismissed = false;
    #finalized = false;
    /** Document scroll before `html[scroll-lock]` (overflow: hidden often resets the viewport). */
    #scrollXBeforeLock = 0;
    #scrollYBeforeLock = 0;
    #ownsScrollLock = false;

    /** @type {(() => void) | null} */
    #boundWheel = null;
    /** @type {(() => void) | null} */
    #boundTouchMove = null;
    /** @type {((e: KeyboardEvent) => void) | null} */
    #boundKeyDown = null;
    /** @type {((e: TransitionEvent) => void) | null} */
    #boundTransitionEnd = null;
    /** @type {(() => void) | null} */
    #boundHashChange = null;

    connectedCallback() {
      try {
        if (HomepageIntro.#isEmailSignupHash()) {
          document.documentElement.classList.add('homepage-intro--email-signup-target');
          this.#removeIntroFromDom();
          return;
        }

        this.#boundHashChange = () => {
          if (!HomepageIntro.#isEmailSignupHash() || this.#dismissed || this.#finalized) return;
          document.documentElement.classList.add('homepage-intro--email-signup-target');
          this.#removeIntroFromDom();
        };
        window.addEventListener('hashchange', this.#boundHashChange);

        this.#revealBackgroundMediaWhenReady();

        const once = this.dataset.once === 'true';
        const sectionId = this.dataset.sectionId || 'default';
        const storageKey = `homepage-intro-${sectionId}`;

        if (once && sessionStorage.getItem(storageKey) === '1') {
          this.#removeIntroFromDom();
          return;
        }

        this.#scrollXBeforeLock = window.scrollX;
        this.#scrollYBeforeLock = window.scrollY;
        document.documentElement.setAttribute('scroll-lock', '');
        this.#ownsScrollLock = true;

        const ms = Math.max(1000, Number(this.dataset.autoDismissMs) || 5000);
        this.#timeoutId = setTimeout(() => this.dismiss(), ms);

        this.#boundWheel = () => this.dismiss();
        this.#boundTouchMove = () => this.dismiss();
        this.#boundKeyDown = (e) => {
          if (e.key === 'Escape') this.dismiss();
        };

        document.addEventListener('wheel', this.#boundWheel, WHEEL_OPTS);
        document.addEventListener('touchmove', this.#boundTouchMove, { passive: true });
        document.addEventListener('keydown', this.#boundKeyDown);

        this.#boundTransitionEnd = (e) => {
          if (e.target !== this) return;
          if (e.propertyName !== 'transform') return;
          if (!this.classList.contains('homepage-intro--leaving')) return;
          this.#clearFallbackDismiss();
          this.#finalize(storageKey, once);
        };
        this.addEventListener('transitionend', this.#boundTransitionEnd);
      } catch {
        this.#releaseScrollLock();
      }
    }

    #revealBackgroundMediaWhenReady() {
      const root = this.querySelector('.homepage-intro__media-root');
      if (!root || !(root instanceof HTMLElement)) return;

      const markLoaded = () => {
        root.classList.add('homepage-intro__media--loaded');
      };

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        markLoaded();
        return;
      }

      const img = root.querySelector('.background-image-container img');
      if (img instanceof HTMLImageElement) {
        if (img.complete && img.naturalWidth > 0) {
          markLoaded();
        } else {
          img.addEventListener('load', markLoaded, { once: true });
          img.addEventListener('error', markLoaded, { once: true });
        }
        return;
      }

      const video = root.querySelector('video-background-component video');
      if (video instanceof HTMLVideoElement) {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          markLoaded();
        } else {
          video.addEventListener('canplay', markLoaded, { once: true });
          video.addEventListener('error', markLoaded, { once: true });
        }
        return;
      }

      markLoaded();
    }

    disconnectedCallback() {
      this.#clearTimeout();
      this.#clearFallbackDismiss();
      this.#removeIntentListeners();
      this.#removeTransitionListener();
      this.#removeHashChangeListener();
      this.#releaseScrollLock();
    }

    dismiss() {
      if (this.#dismissed) return;
      this.#dismissed = true;
      this.#clearTimeout();
      this.#removeIntentListeners();

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const sectionId = this.dataset.sectionId || 'default';
      const storageKey = `homepage-intro-${sectionId}`;
      const once = this.dataset.once === 'true';

      try {
        this.setAttribute('aria-hidden', 'true');
      } catch {
        /* ignore */
      }

      if (reduceMotion) {
        this.#finalize(storageKey, once);
        return;
      }

      this.classList.add('homepage-intro--leaving');

      const slideMs = Math.max(200, Number(this.dataset.slideDurationMs) || 650);
      this.#fallbackDismissId = window.setTimeout(() => {
        if (!this.#finalized) this.#finalize(storageKey, once);
      }, slideMs + 400);
    }

    /**
     * @param {string} storageKey
     * @param {boolean} once
     */
    #finalize(storageKey, once) {
      if (this.#finalized) return;
      this.#finalized = true;
      this.#clearFallbackDismiss();
      this.#removeTransitionListener();
      this.#releaseScrollLock();
      if (once) {
        try {
          sessionStorage.setItem(storageKey, '1');
        } catch {
          /* ignore */
        }
      }
      this.#removeIntroFromDom();
    }

    /** Remove the theme section wrapper (fixed full-screen hit target), not only the custom element. */
    #removeIntroFromDom() {
      const sectionRoot = this.closest('.shopify-section');
      try {
        if (sectionRoot) {
          sectionRoot.remove();
          return;
        }
        this.remove();
      } catch {
        /* ignore */
      }
    }

    #releaseScrollLock() {
      if (!this.#ownsScrollLock) return;
      this.#ownsScrollLock = false;
      const x = this.#scrollXBeforeLock;
      const y = this.#scrollYBeforeLock;
      try {
        document.documentElement.removeAttribute('scroll-lock');
      } catch {
        /* ignore */
      }
      requestAnimationFrame(() => {
        window.scrollTo(x, y);
      });
    }

    #clearTimeout() {
      if (this.#timeoutId != null) {
        clearTimeout(this.#timeoutId);
        this.#timeoutId = null;
      }
    }

    #clearFallbackDismiss() {
      if (this.#fallbackDismissId != null) {
        clearTimeout(this.#fallbackDismissId);
        this.#fallbackDismissId = null;
      }
    }

    #removeIntentListeners() {
      if (this.#boundWheel) {
        document.removeEventListener('wheel', this.#boundWheel, WHEEL_OPTS);
        this.#boundWheel = null;
      }
      if (this.#boundTouchMove) {
        document.removeEventListener('touchmove', this.#boundTouchMove);
        this.#boundTouchMove = null;
      }
      if (this.#boundKeyDown) {
        document.removeEventListener('keydown', this.#boundKeyDown);
        this.#boundKeyDown = null;
      }
    }

    #removeTransitionListener() {
      if (this.#boundTransitionEnd) {
        this.removeEventListener('transitionend', this.#boundTransitionEnd);
        this.#boundTransitionEnd = null;
      }
    }

    #removeHashChangeListener() {
      if (this.#boundHashChange) {
        window.removeEventListener('hashchange', this.#boundHashChange);
        this.#boundHashChange = null;
      }
    }
  }

  customElements.define('homepage-intro', HomepageIntro);
})();
